import debounce from 'lodash/debounce';
import React, { useCallback, useRef, useState } from 'react';
import { ToastContainer } from 'react-toastify';

import { Head } from '@/components/Head';
import { LocationMap } from '@/components/LocationMap';
import { useCurrency } from '@/providers/CurrencyProvider';
import { notifyError, notifySuccess } from '@/utils/notifications';

import { ProjectSlotFilters } from '../api';
import PurchaseSlotModal from '../components/PurchaseSlot';
import { useGetProjectSlots, usePurchaseSlot } from '../hooks';

const SECTORS = [
  'All',
  'Agriculture',
  'Technology',
  'Energy',
  'Health',
  'Real Estate',
  'Finance',
  'Education',
  'Infrastructure',
  'Entertainment',
];

const riskColors: Record<string, string> = {
  Low: 'bg-green-100 text-green-700',
  Medium: 'bg-yellow-100 text-yellow-700',
  High: 'bg-red-100 text-red-700',
};

export const ProjectSlots = () => {
  const { formatCurrency } = useCurrency();

  const [isPurchaseOpen, setIsPurchaseOpen] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [purchasingSlot, setPurchasingSlot] = useState<any | null>(null);
  const [viewingSlot, setViewingSlot] = useState<any | null>(null);
  const [filters, setFilters] = useState<ProjectSlotFilters>({});
  const [debouncedFilters, setDebouncedFilters] = useState<ProjectSlotFilters>(
    {},
  );
  const [selectedSector, setSelectedSector] = useState('All');

  const applyDebounced = useRef(
    debounce((f: ProjectSlotFilters) => setDebouncedFilters(f), 400),
  ).current;

  const handleChange = useCallback(
    (field: keyof ProjectSlotFilters, value: string) => {
      const next = { ...filters, [field]: value };
      setFilters(next);
      applyDebounced(next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters],
  );

  const handleSectorChange = (sector: string) => {
    setSelectedSector(sector);
    const next = {
      ...filters,
      sector: sector === 'All' ? '' : sector,
    };
    setFilters(next);
    applyDebounced(next);
  };

  const handleClear = () => {
    setFilters({});
    setDebouncedFilters({});
    setSelectedSector('All');
    applyDebounced.cancel();
  };

  const hasFilters =
    Object.values(filters).some(Boolean) || selectedSector !== 'All';

  const {
    mutate: purchaseSlot,
    isLoading: isPurchaseLoading,
    isSuccess: isPurchaseSuccess,
    isError: isPurchaseError,
    error: purchaseError,
    reset: purchaseReset,
  } = usePurchaseSlot();

  const { data, isLoading } = useGetProjectSlots(debouncedFilters);

  if (isPurchaseSuccess) {
    notifySuccess('Slot purchased successfully!', 2000);
    purchaseReset();
  }

  if (isPurchaseError) {
    const err = purchaseError as any;
    const msg =
      err?.response?.data?.errors?.[0]?.msg ||
      err?.message ||
      'Purchase failed. Please try again.';
    notifyError(msg, false);
    purchaseReset();
  }

  return (
    <div className='mt-4 sm:mt-0'>
      <Head title='Project Slots (APS)' />
      <h3 className='text-xl font-semibold px-4 pt-2 text-gray-800'>
        Awap Project Slots
      </h3>
      <p className='px-4 text-sm text-gray-500 mb-2'>
        Invest in curated projects and earn appreciation returns
      </p>

      {/* Filters */}
      <div className='flex flex-wrap gap-3 px-4 py-4 bg-gray-50 border-b'>
        <input
          type='text'
          placeholder='Search by name…'
          value={filters.name ?? ''}
          onChange={(e) => handleChange('name', e.target.value)}
          className='flex-1 min-w-[160px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400'
        />
        <select
          aria-label='Filter by sector'
          value={selectedSector}
          onChange={(e) => handleSectorChange(e.target.value)}
          className='border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400'
        >
          {SECTORS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          type='number'
          placeholder='Min price'
          value={filters.minPrice ?? ''}
          onChange={(e) => handleChange('minPrice', e.target.value)}
          className='w-[120px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400'
        />
        <input
          type='number'
          placeholder='Max price'
          value={filters.maxPrice ?? ''}
          onChange={(e) => handleChange('maxPrice', e.target.value)}
          className='w-[120px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400'
        />
        {hasFilters && (
          <button
            type='button'
            onClick={handleClear}
            className='px-4 py-2 text-sm text-gray-500 border border-gray-300 rounded-lg hover:text-gray-800'
          >
            Clear
          </button>
        )}
      </div>

      {/* Cards */}
      {isLoading ? (
        <div className='p-8 text-center text-gray-400'>Loading...</div>
      ) : !data?.length ? (
        <div className='p-8 text-center text-gray-400'>
          No project slots available.
        </div>
      ) : (
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 p-4'>
          {data.map((slot: any) => (
            <div
              key={slot._id}
              className='bg-white shadow-md rounded-2xl overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col'
            >
              <img
                src={slot.picture}
                alt={slot.name}
                className='w-full h-40 object-cover'
              />

              <div className='p-4 space-y-2 flex-1 flex flex-col'>
                <div className='flex items-start justify-between gap-2'>
                  <h2 className='text-base font-semibold text-gray-800 leading-tight'>
                    {slot.name}
                  </h2>
                  <button
                    type='button'
                    className='text-sm font-medium text-blue-600 hover:underline shrink-0'
                    onClick={() => setViewingSlot(slot)}
                  >
                    View
                  </button>
                </div>

                <div className='flex items-center gap-2 flex-wrap'>
                  <span className='text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded'>
                    {slot.sector}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded font-medium ${riskColors[slot.riskLevel] ?? 'bg-gray-100 text-gray-600'}`}
                  >
                    {slot.riskLevel} Risk
                  </span>
                </div>

                {slot.location && (
                  <p className='text-xs text-gray-500 flex items-center gap-1'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-3.5 w-3.5 shrink-0 text-gray-400'
                      viewBox='0 0 24 24'
                      fill='currentColor'
                    >
                      <path
                        fillRule='evenodd'
                        d='M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-2.003 3.5-4.697 3.5-8.327a8.25 8.25 0 00-16.5 0c0 3.63 1.556 6.324 3.5 8.327a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.144.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z'
                        clipRule='evenodd'
                      />
                    </svg>
                    {slot.location}
                  </p>
                )}

                <p className='text-gray-600 text-sm'>
                  {slot.availableSlots} slot(s) left
                </p>

                {/* Funding progress bar */}
                <div>
                  <div className='flex justify-between text-xs text-gray-500 mb-1'>
                    <span>Funded</span>
                    <span>{slot.fundingProgress ?? 0}%</span>
                  </div>
                  <div className='w-full bg-gray-200 rounded-full h-1.5'>
                    <div
                      className='bg-blue-500 h-1.5 rounded-full w-[var(--progress)]'
                      style={
                        {
                          '--progress': `${slot.fundingProgress ?? 0}%`,
                        } as React.CSSProperties
                      }
                    />
                  </div>
                </div>

                <p className='text-xs text-gray-500'>
                  ↑ {slot.appreciationRate}% appreciation
                </p>
                <p className='text-xs text-gray-500'>
                  Duration:{' '}
                  <span className='font-medium text-gray-700'>
                    {slot.projectDuration}
                  </span>
                </p>

                <p className='text-xs text-gray-500'>
                  Total cost:{' '}
                  <span className='font-medium text-gray-700'>
                    {formatCurrency(slot.projectCost)}
                  </span>
                </p>

                <div className='flex items-center justify-between pt-2 mt-auto'>
                  <span className='text-lg font-bold text-blue-600'>
                    {formatCurrency(slot.slotPrice)}
                    <span className='text-xs font-normal text-gray-400'>
                      /slot
                    </span>
                  </span>
                  <button
                    type='button'
                    onClick={() => {
                      setSelectedSlotId(slot._id);
                      setPurchasingSlot(slot);
                      setIsPurchaseOpen(true);
                    }}
                    disabled={!slot.isAvailable || slot.availableSlots === 0}
                    className={`text-sm font-medium px-3 py-1 rounded-full ${
                      slot.isAvailable && slot.availableSlots > 0
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {slot.isAvailable && slot.availableSlots > 0
                      ? 'Purchase'
                      : 'Sold Out'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Detail Modal */}
      {viewingSlot && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
          <div className='bg-white w-full max-w-2xl rounded-2xl shadow-xl relative flex flex-col max-h-[90vh]'>
            <button
              type='button'
              onClick={() => setViewingSlot(null)}
              className='absolute top-3 right-3 z-10 text-gray-400 hover:text-gray-700'
            >
              ✕
            </button>

            <div className='overflow-y-auto flex-1 p-6'>
              <img
                src={viewingSlot.picture}
                alt={viewingSlot.name}
                className='w-full h-48 object-cover rounded-xl mb-4'
              />
              <h2 className='text-2xl font-bold text-gray-800'>
                {viewingSlot.name}
              </h2>
              <div className='flex gap-2 mt-1 flex-wrap'>
                <span className='text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded'>
                  {viewingSlot.sector}
                </span>
                <span
                  className={`text-xs px-2 py-1 rounded font-medium ${riskColors[viewingSlot.riskLevel]}`}
                >
                  {viewingSlot.riskLevel} Risk
                </span>
              </div>

              {viewingSlot.location && (
                <LocationMap location={viewingSlot.location} />
              )}

              <div className='mt-4 space-y-2 text-sm text-gray-700'>
                <p>
                  <strong>Sector:</strong> {viewingSlot.sector}
                </p>
                <p>
                  <strong>Project Cost:</strong>{' '}
                  {formatCurrency(viewingSlot.projectCost)}
                </p>
                <p>
                  <strong>Slot Price:</strong>{' '}
                  {formatCurrency(viewingSlot.slotPrice)}
                </p>
                <p>
                  <strong>Available Slots:</strong> {viewingSlot.availableSlots}
                </p>
                <p>
                  <strong>Project Duration:</strong>{' '}
                  {viewingSlot.projectDuration}
                </p>
                <p>
                  <strong>Appreciation Rate:</strong>{' '}
                  {viewingSlot.appreciationRate}%
                </p>
                <p>
                  <strong>Funding Progress:</strong>{' '}
                  {viewingSlot.fundingProgress}%
                </p>
                <div>
                  <h3 className='font-semibold text-gray-800 mb-1'>
                    Description
                  </h3>
                  <p className='leading-relaxed whitespace-pre-line'>
                    {viewingSlot.description}
                  </p>
                </div>
              </div>
            </div>

            <div className='flex-shrink-0 flex justify-between items-center border-t px-6 py-4'>
              <span className='text-xl font-bold text-blue-600'>
                {formatCurrency(viewingSlot.slotPrice)}
                <span className='text-sm font-normal text-gray-400'>/slot</span>
              </span>
              <button
                type='button'
                onClick={() => {
                  setViewingSlot(null);
                  setSelectedSlotId(viewingSlot._id);
                  setPurchasingSlot(viewingSlot);
                  setIsPurchaseOpen(true);
                }}
                disabled={
                  !viewingSlot.isAvailable || viewingSlot.availableSlots === 0
                }
                className='px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium'
              >
                {viewingSlot.isAvailable && viewingSlot.availableSlots > 0
                  ? 'Purchase Slot'
                  : 'Sold Out'}
              </button>
            </div>
          </div>
        </div>
      )}

      <PurchaseSlotModal
        isOpen={isPurchaseOpen}
        setIsOpen={setIsPurchaseOpen}
        onSubmit={async (formData) => {
          return new Promise<void>((resolve, reject) => {
            purchaseSlot(formData, {
              onSuccess: () => resolve(),
              onError: (err) => reject(err),
            });
          });
        }}
        isLoading={isPurchaseLoading}
        id={selectedSlotId}
        slot={purchasingSlot}
      />

      <ToastContainer />
    </div>
  );
};
